// ./src/hooks/useClients.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Client,
  ClientInput,
  ClientFilter,
  ClientWhereInput,
  ClientService,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { Pagination } from "@/utils/api";

export interface UseClientsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<ClientFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseClientsReturn {
  // Data state
  client: Client | null;
  clients: Client[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ClientFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingClientId: string | null;
  selectedClient: Client | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<ClientFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Client[]>;
  create: (params: ClientInput) => Promise<Client | null>;
  update: (
    where: ClientWhereInput,
    params: ClientInput
  ) => Promise<Client | null>;
  remove: (where: ClientWhereInput) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<ClientFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedClient: (client: Client | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  activeClients: Client[];
  inactiveClients: Client[];
  clientsByStatus: Record<number, Client[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT: SortConfig[] = [{ key: "companyName", direction: "asc" }];

export const useClients = (
  options: UseClientsOptions = {}
): UseClientsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<ClientFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingClientId, setRemovingClientId] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => clients.length === 0 && !loading,
    [clients.length, loading]
  );

  const activeClients = useMemo(() => {
    return clients.filter((client) => client.status === 1);
  }, [clients]);

  const inactiveClients = useMemo(() => {
    return clients.filter((client) => client.status === 0);
  }, [clients]);

  const clientsByStatus = useMemo(() => {
    return clients.reduce((acc, client) => {
      const status = client.status || 0;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(client);
      return acc;
    }, {} as Record<number, Client[]>);
  }, [clients]);

  // Helper function to update client in list if it exists, or add it if not
  const updateOrAddClientInList = useCallback((updatedClient: Client) => {
    setClients((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === updatedClient.id);
      if (existingIndex >= 0) {
        const newClients = [...prev];
        newClients[existingIndex] = updatedClient;
        return newClients;
      } else {
        return [updatedClient, ...prev];
      }
    });
  }, []);

  // Helper function to remove client from list if it exists
  const removeClientFromList = useCallback((clientId: string) => {
    setClients((prev) => prev.filter((c) => c.id !== clientId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setClient(null);
    setClients([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingClientId(null);
    setSelectedClient(null);
  }, []);

  // Get single client (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await ClientService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const clientData = result.data[0] as Client;
          setClient(clientData);
          updateOrAddClientInList(clientData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load client";
          setLastError(errorMessage);
          setClient(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load client";
        setLastError(errorMessage);
        setClient(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddClientInList]
  );

  // List clients (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<ClientFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setClients([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await ClientService.list(
          filter as ClientFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newClients = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setClients((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const uniqueNewClients = newClients.filter(
                (c) => !existingIds.has(c.id)
              );
              return [...prev, ...uniqueNewClients];
            });
          } else {
            setClients(newClients);
          }

          setHasMore(newClients.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load clients";
          setLastError(errorMessage);
          if (!isAppending) setClients([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load clients";
        setLastError(errorMessage);
        if (!isAppending) setClients([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many clients by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Client[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await ClientService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedClients = Array.isArray(result.data) ? result.data : [];
          loadedClients.forEach((client) => {
            updateOrAddClientInList(client);
          });
          setLastError(null);
          return loadedClients;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load clients";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load clients";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddClientInList]
  );

  // Create client (matching API method name)
  const create = useCallback(
    async (params: ClientInput): Promise<Client | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await ClientService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newClient = result.data[0] as Client;
          setClient(newClient);
          setClients((prev) => [newClient, ...prev]);
          setLastError(null);
          return newClient;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create client";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create client";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update client (matching API method name)
  const update = useCallback(
    async (
      where: ClientWhereInput,
      params: ClientInput
    ): Promise<Client | null> => {
      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ClientService.update(where.id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedClient = result.data[0] as Client;
          setClient(updatedClient);
          updateOrAddClientInList(updatedClient);
          setLastError(null);
          return updatedClient;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update client";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update client";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddClientInList]
  );

  // Remove client (matching API method name)
  const remove = useCallback(
    async (where: ClientWhereInput): Promise<boolean> => {
      const clientId = where.id;
      if (!clientId) {
        setLastError("Client ID is required for removal");
        return false;
      }

      setRemovingClientId(clientId);
      setLastError(null);

      try {
        const result = await ClientService.remove(clientId);

        if (result.code === OP_RESULT_CODES.OK) {
          setClient(null);
          removeClientFromList(clientId);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove client";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove client";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingClientId(null);
      }
    },
    [removeClientFromList]
  );

  // Load more clients
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh clients
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    client,
    clients,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingClientId,
    selectedClient,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedClient,
    reset,
    activeClients,
    inactiveClients,
    clientsByStatus,
    lastError,
    clearError,
  };
};
